import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/tiptap/styles.css";

import { I18nProvider } from "@lingui/react";
import { LoadingOverlay, MantineProvider, rem, v8CssVariablesResolver } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";

import "./config/httpClientConfig";
import "./config/dayjsConfig";

import { i18n } from "@/i18n";
import CookieConsentBanner from "./components/common/CookieConsentBanner/CookieConsentBanner";
import ErrorBoundary from "./components/common/ErrorBoundary/ErrorBoundary";
import ErrorServer from "./components/common/ErrorBoundary/ErrorServer";
import { uiStore, userStore } from "./mobx/rootStore";
import { RoutesGroup } from "./routes";
import { isPublicPath, routesList } from "./routesList";

import "driver.js/dist/driver.css";
import { useLingui } from "@lingui/react/macro";
import { notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useReducer, useState } from "react";
import { logout } from "@/utils/auth/logout";

import "./index.css";
import BadgeClasses from "./styles/components/Badge.module.css";

const DEFAULT_FONT_FAMILY = "EconomosFont, Arial, ui-sans-serif, sans-serif";
const NOTIFICATIONS_BOTTOM_SPACE = 110;

const initReducer = (state, action) => {
	switch (action.type) {
		case "ready":
			return { status: "ready", error: action.error ?? null };
		default:
			return state;
	}
};

function AppContent() {
	const { t } = useLingui();

	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: true,
						staleTime: 5_000,
						retry: 1,
					},
				},
			}),
	);

	const [initState, dispatch] = useReducer(initReducer, {
		status: "loading",
		error: null,
	});

	useEffect(() => {
		(async () => {
			const isPublic = isPublicPath(window.location.pathname);
			try {
				const result = await userStore.init();
				if (result?.error === "server") {
					if (isPublic) {
						// Don't block public routes (login/register/repair) on server errors.
						dispatch({ type: "ready" });
					} else {
						dispatch({ type: "ready", error: "server" });
					}
					return;
				}
				if (result?.error === "auth") {
					if (!isPublic) {
						notifications.show({
							title: t`Sesión expirada`,
							message: t`Necesitas iniciar sesión`,
							color: "orange",
						});
						logout({
							redirect: `${routesList.login.path}?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`,
						});
						return;
					}
					dispatch({ type: "ready" });
					return;
				}
			} catch (error) {
				console.error("Error initializing user:", error);
				dispatch({ type: "ready", error: "server" });
				return;
			}
			// Sincroniza locale persistido
			import("dayjs").then(({ default: dayjs }) => dayjs.locale(uiStore.language));
			i18n.activate(uiStore.language);
			dispatch({ type: "ready" });
		})();
	}, []);

	return (
		<ErrorBoundary>
			<Notifications />
			<ModalsProvider>
				<QueryClientProvider client={queryClient}>
					<BrowserRouter future={{ v7_startTransition: true }}>
						{initState.error === "server" ? (
							<ErrorServer />
						) : initState.status === "loading" ? (
							<LoadingOverlay
								visible={true}
								overlayProps={{
									radius: "sm",
									blur: 1,
								}}
								zIndex={1000}
							/>
						) : (
							<Suspense
								fallback={
									<LoadingOverlay
										visible={true}
										overlayProps={{
											radius: "sm",
											blur: 1,
										}}
										zIndex={1000}
									/>
								}
							>
								<RoutesGroup />
							</Suspense>
						)}
						<CookieConsentBanner />
					</BrowserRouter>
					{import.meta.env.DEV && !window.__HIDE_DEVTOOLS && (
						<ReactQueryDevtools initialIsOpen={false} />
					)}
				</QueryClientProvider>
			</ModalsProvider>
		</ErrorBoundary>
	);
}

export default function App() {
	return (
		<MantineProvider
			defaultColorScheme="dark"
			withGlobalClasses
			// Restores Mantine 8 colors for light/subtle/outline variants (Módulos nav, etc.)
			cssVariablesResolver={v8CssVariablesResolver}
			// https://github.com/mantinedev/mantine/blob/2f91229da10d30a205ade277161bc8829740bd93/packages/%40mantine/core/src/core/MantineProvider/default-theme.ts
			theme={{
				fontFamily: DEFAULT_FONT_FAMILY,

				scale: 1, // https://mantine.dev/styles/rem/#rem-units-scaling

				primaryColor: "indigo",

				fontSizes: {
					n: rem(6),
					xxxs: rem(8),
					xxs: rem(10),
					xs: rem(12),
					sm: rem(14),
					md: rem(16),
					lg: rem(18),
					xl: rem(20),
					xxl: rem(22),
					xxxl: rem(24),
					g: rem(26),
				},

				spacing: {
					n: rem(2),
					xxxs: rem(4),
					xxs: rem(8),
					xs: rem(10),
					sm: rem(12),
					md: rem(16),
					lg: rem(20),
					xl: rem(32),
					xxl: rem(36),
					xxxl: rem(40),
				},

				breakpoints: {
					xs: "36em",
					sm: "48em",
					md: "62em",
					lg: "75em",
					xl: "88em",
				},

				defaultRadius: "md",

				components: {
					AppShell: {
						styles: {
							main: {
								// E2E test mode (window.__HIDE_DEVTOOLS): no bottom padding so
								// screenshots scrolled to the end don't show empty space
								paddingBottom: window.__HIDE_DEVTOOLS
									? 0
									: `calc(var(--app-shell-footer-offset, 0rem) + var(--app-shell-padding, 1rem) + ${rem(NOTIFICATIONS_BOTTOM_SPACE)})`,
							},
						},
					},
					LoadingOverlay: {
						defaultProps: {
							overlayProps: { blur: 4, backgroundOpacity: 0 },
							loaderProps: { type: "bars" },
						},
					},
					Badge: {
						classNames: {
							root: BadgeClasses.root,
						},
					},
				},
			}}
		>
			<I18nProvider i18n={i18n}>
				<AppContent />
			</I18nProvider>
		</MantineProvider>
	);
}
